/**
 * Validation module for the Conference Room Designer.
 * This module contains functions to validate various aspects of the room design and equipment setup.
 * @module conferenceRoomDesignerValidation
 */
import toolIndex from "toolConstants";
import ReducersIndex from "reduxModules/ducks";
import { inRange, side, calcMaxTilesFit, snapDownToIncrement } from 'utilities/mathematic';
import { toFeet, distanceToString } from "utilities/format";
import { indexOf, round, findKey, has, keys } from "lodash";
import { showInfo, showWarning, showError } from "utilities/toastManager";

// All toast messages now use the global queue in toastManager

// Track if we're currently validating microphones to prevent infinite loops
let isValidatingMicrophones = false;
// Same reentrancy guard, scoped to the ceiling EXPANSION mic (dcm_x) validation below.
let isValidatingExpansionMicrophones = false;

/*
ERROR CODES 
1000: Area Dims
1010: Area Ceiling Height

1100: Mic Style
1110: Pendant Height

1200: couldn't center one or more devices
1300: collisions between devices 
1400: fixed location warining

2000: ProductFamily
*/


/**
 * Object containing validation functions for different aspects of the room design.
 * Each function is called from the errorMiddleware and receives dispatch and state as parameters.
 * @type {Object.<string, function>}
 */
const validate = {
  /** Validates the room dimensions, ceiling height, and microphone setup based on the current units.
   * @function 
   * @param {function} dispatch - The Redux dispatch function.
   * @param {Object} state - The current Redux state. 
   * @description
   * This function checks for specific error codes in the state and calls the appropriate validation functions.   
   * This ensures the error messages update on unit change
   */
  units: (dispatch, state) => {
    const { error  } = state.ui;
    if (has(error, '1000')) {
      validate.dimensions(dispatch, state);
    } 
    if (has(error, '1010')) {
      validate.ceilingHeight(dispatch, state);
    } 
    if (has(error, '1050')) {
      validate.table(dispatch, state);
    }
    if (has(error, '1110')) {
      validate.microphones(dispatch, state);
    } 
  },

  /**
   * Validates table dimensions based on the table shape.
   * @function
   * @param {function} dispatch - The Redux dispatch function.
   * @param {Object} state - The current Redux state.
   * 
   * @description
   * Checks if the table dimensions meet the minimum requirements for the selected shape.
   * Dispatches an error action if dimensions are below minimum values.
   */
  table: (dispatch, state) => {
    const { units } = state.ui;
    const isMeter = units === 'meters';
    const activeArea = state.container.areas[state.container.activeTab];
    const table = activeArea?.table;
    
    if (!table || !table.shape) {
      // No table or shape defined, nothing to validate
      dispatch(ReducersIndex.uiReducers.setError({
        code: 1050,
      }));
      return;
    }
    
    const tableConstants = toolIndex[state.container.currentTool].constants.tables[table.shape];
    if (!tableConstants) {
      return;
    }
    
    const minLength = tableConstants.minimum.length;
    const minWidth = tableConstants.minimum.width;
    const currentLength = table.size?.length || 0;
    const currentWidth = table.size?.width || 0;
    
    // Check if dimensions are below minimum (using negative values from inRange convention)
    const lengthBelowMin = currentLength < minLength ? -1 : 0;
    const widthBelowMin = (table.shape !== 'round' && currentWidth < minWidth) ? -1 : 0;
    
    const errorCode = 1050;
    const errorParams = [...(lengthBelowMin ? ['size.length'] : []), ...(widthBelowMin ? ['size.width'] : [])];
    
    if (lengthBelowMin || widthBelowMin) {
      const shapeName = table.shape.charAt(0).toUpperCase() + table.shape.slice(1);
      let errorMsg = '';
      
      if (table.shape === 'round') {
        errorMsg = `${shapeName} table diameter can't be smaller than ${distanceToString(minLength, isMeter)}`;
      } else if (lengthBelowMin && widthBelowMin) {
        errorMsg = `${shapeName} table dimensions can't be smaller than ${distanceToString(minLength, isMeter)} x ${distanceToString(minWidth, isMeter)}`;
      } else if (lengthBelowMin) {
        errorMsg = `${shapeName} table length can't be smaller than ${distanceToString(minLength, isMeter)}`;
      } else {
        errorMsg = `${shapeName} table width can't be smaller than ${distanceToString(minWidth, isMeter)}`;
      }
      
      // Follow same pattern as dimensions validation - set error with reset flag
      dispatch(ReducersIndex.uiReducers.setError({
        code: errorCode,
        params: errorParams,
        msg: errorMsg,
        reset: true  // Reset to previous valid value, same as dimensions
      }));
    } else {
      // Clear table error if dimensions are valid
      dispatch(ReducersIndex.uiReducers.setError({
        code: errorCode,
      }));
    }
  },

  /**
   * Validates the room dimensions.
   * @function
   * @param {function} dispatch - The Redux dispatch function.
   * @param {Object} state - The current Redux state.
   * 
   * @description
   * Checks if the room length and width are within the allowed range.
   * Dispatches an error action if dimensions are invalid, with specific error codes and parameters.
   */
  dimensions: (dispatch, state) => {
    const { units } = state.ui
    const isMeter = units === 'meters';
    const { length, width } = (state.container.areas[state.container.activeTab]).dimensions;
    const { area: { min, max }, tool: { areaName: name } } = toolIndex[state.container.currentTool].constants;
    
    const e1 = inRange(length, min, max);
    const e2 = inRange(width, min, max);
    
    const errorCode = 1000;
    const errorParams = [...(e1 ? ['length'] : []), ...(e2 ? ['width'] : [])];
    if (e1 || e2) {
      if (e1 === e2 || e1 === 0 || e2 === 0) {
        dispatch(ReducersIndex.uiReducers.setError({
          code: errorCode,
          params: errorParams,
          msg: `Dimension can't be ${(e1 || e2) < 0 ? 'smaller' : 'larger'} than ${isMeter ? ((e1 || e2) < 0 ? min.toFixed(1) : max.toFixed(1)) : ((e1 || e2) < 0 ? toFeet(min) : toFeet(max))} ${isMeter ? 'Meters' : 'Feet'}`,
          reset: true
        }))
      } else {
        dispatch(ReducersIndex.uiReducers.setError({
          code: errorCode,
          params: errorParams,
          msg: `Dimension must be between ${isMeter ? min.toFixed(1) + ' and ' + max.toFixed(1) : toFeet(min) + ' and ' + toFeet(max)} ${isMeter ? 'Meters' : 'Feet'}`,
          reset: true
        }))
      };
    } else {
      dispatch(ReducersIndex.uiReducers.setError({
        code: errorCode,
      }));
    };
  },

  /**
   * Validates the ceiling height.
   * @function
   * @param {function} dispatch - The Redux dispatch function.
   * @param {Object} state - The current Redux state.
   * 
   * @description
   * Checks if the ceiling height is within the allowed range.
   * Dispatches an error action if the height is invalid.
   * Also triggers validation for microphones and speakers as ceiling height affects them.
   */
  ceilingHeight: (dispatch, state) => {
    const { units } = state.ui;
    const isMeter = units === 'meters';
    const { ceilingHeight: { min: ceilingHeight } } = state.container.areas[state.container.activeTab];
    const { ceiling: { min, max }, tool: { areaName: name } } = toolIndex[state.container.currentTool].constants;
    
    // Add small tolerance for floating point precision issues (0.0001 meters = 0.1mm)
    const tolerance = 0.0001;
    const adjustedMin = min - tolerance;
    const adjustedMax = max + tolerance;
    const e1 = inRange(ceilingHeight, adjustedMin, adjustedMax);
    const errorCode = 1010;
    const errorParams = e1 ? ['min'] : [];
    if (e1) {
      // Round displayed bounds so they're reachable: floor for max (4.572 → "4.5"), ceil for min (2.4384 → "2.5").
      // Plain toFixed(1) rounds to nearest, which produces a max of "4.6 m" but 4.6 m exceeds the actual 4.572 m / 15 ft limit.
      // Also fix the feet conversion — `toFeet(value)` without the `isMeter=true` flag returns the meter value unchanged.
      const maxMeters = (Math.floor(max * 10) / 10).toFixed(1);
      const minMeters = (Math.ceil(min * 10) / 10).toFixed(1);
      const maxFeet = Math.round(toFeet(max, true));
      const minFeet = Math.round(toFeet(min, true));
      const limit = isMeter ? (e1 < 0 ? minMeters : maxMeters) : (e1 < 0 ? minFeet : maxFeet);
      dispatch(ReducersIndex.uiReducers.setError({
        code: errorCode,
        params: errorParams,
        msg: `Room Ceiling can't be ${e1 < 0 ? 'lower' : 'higher'} than ${limit} ${isMeter ? 'Meters' : 'Feet'}`,
        reset: true
      }))
    } else {
      dispatch(ReducersIndex.uiReducers.setError({
        code: errorCode,
      }))
      validate.microphones(dispatch, state);
      validate.speakers(dispatch, state);
      validate.expansionMicrophones(dispatch, state);
    }
  },

  /**
   * Validates the microphone setup.
   * @function
   * @param {function} dispatch - The Redux dispatch function.
   * @param {Object} state - The current Redux state.
   *
   * @description
   * Checks if the microphone height and model are appropriate for the room acoustics and ceiling height.
   * Adjusts microphone settings if needed and dispatches error or info actions accordingly.
   */
  microphones: (dispatch, state) => {
    // Prevent infinite validation loop - skip if we're already validating
    if (isValidatingMicrophones) {
      return;
    }

    // Set flag at the start
    isValidatingMicrophones = true;

    try {
      const { units, isModalOpen, info } = state.ui;
      const isMeter = units === 'meters';
      const area = state.container.areas[state.container.activeTab];

      // Defensive check for missing area data
      if (!area || !area.microphones || !area.ceilingHeight) {
        return;
      }
    
    const { microphones: { model, maxHeight, height }, acoustics: currentAcoustics, ceilingHeight: { min: ceilingHeight } } = area;
    const { equipment: { microphones: { [model]: { micData } }, micCategories }, acoustics, area: { talkerHeight } } = toolIndex[state.container.currentTool].constants;
    const isCeilingMic = micCategories.CEILING_MOUNTED.has(model);
    const isPendantMic = micCategories.PENDANT.has(model);
    // PIG-422: BMA360D is a recessed ceiling-tile mic with no pipe-mount limit. Its spec
    // max install height is 20', so it must be exempt from the 13' MAX_CEILING_MIC_HEIGHT
    // clamp below — that cap exists to protect pole-mounted ceiling mics only.
    const isCeilingTileMic = micCategories.CEILING_TILE?.has(model) ?? false;
    // CBC 2500 CM/PM (BAR category) are also CEILING_MOUNTED, but the `max`/`snappedMax`
    // formula below (side(micDataValue, 1) + talkerHeight) models an omnidirectional ceiling
    // puck's coverage radius vs. mounting height trade-off — it has nothing to do with a CBC
    // bar's directional stadium-shaped pickup pattern. CBC bars have their own height rule
    // (calculateCBCHeight: ceiling - 0.5', clamped 6'-12') enforced directly in the
    // setMicrophones reducer via heightRange, so they must be exempt from e0/e1/e3-e6 below —
    // otherwise this generic validation silently overrides the correct CBC default with a
    // much lower "optimal acoustic coverage" height meant for TCM-X/DCM-X pucks.
    const isBarMic = micCategories.BAR?.has(model) ?? false;

    const acousticsIndex = indexOf(acoustics, currentAcoustics);
    const micDataValue = micData[acousticsIndex];
    const max = round(side(micDataValue, 1) + talkerHeight, 4);
    // Snap max DOWN to the same 0.5 ft pole increment the setMicrophones reducer uses.
    // `max` is a ceiling on the mount height (the highest mount keeping the coverage radius
    // >= 1 m), so it must never be rounded UP past itself — see the matching comment in
    // logic.js's resetArea 'acoustics' case.
    const poleIncrement = 0.1524;
    const snappedMax = snapDownToIncrement(max, poleIncrement);

    // Check for invalid calculations
    if (!isFinite(max) || acousticsIndex === -1 || !micDataValue) {
      console.error('🔴 DESIGN ERROR - Invalid microphone validation calculations:', {
        micModel: model,
        currentAcoustics,
        acousticsIndex,
        acousticsFound: acousticsIndex !== -1,
        micDataValue,
        talkerHeight,
        max,
        isFiniteMax: isFinite(max)
      });
    }
    // Update microphone height if required
    // Use tolerance to avoid floating point precision issues causing infinite loops
    // Skip for CBC bars — `max` here is the TCM-X-style coverage formula, not meaningful
    // for CBC's own ceiling-0.5'/6'-12' height rule (see isBarMic comment above).
    const tolerance = 0.0001;
    if (!isBarMic && Math.abs(max - maxHeight) > tolerance) {
      dispatch(ReducersIndex.container.setMicrophones({ maxHeight: max }))
    };

    // REMOVED: Line that forced ceiling mic height to equal ceiling height
    // This was preventing adjustable height for ceiling microphones
    // if ((micModel === 'ceiling_mic' && height !== ceilingHeight) || (height > ceilingHeight && height <= max)) {
    //   dispatch(ReducersIndex.container.setMicrophones({ height: ceilingHeight }))
    // };
    // Check for errors
    const toolConstants = toolIndex[state.container.currentTool]?.constants;

    // NOTE: heightRange is calculated dynamically in room.ts reducer (lines 538-540)
    // It's NOT stored in state, so don't try to dispatch it here
    // Dispatching it causes unnecessary setMicrophones calls that can reset user's manual height adjustments

    const ceilingThreshold = toolConstants?.ceiling?.microphoneThreshold || 4.2672; // 14 feet fallback
    const MAX_CEILING_MIC_HEIGHT = 3.9624; // 13 feet - maximum allowed ceiling mic mounting height
    const e0 = isCeilingMic && !isPendantMic && !isCeilingTileMic && !isBarMic && ceilingHeight >= ceilingThreshold && height > MAX_CEILING_MIC_HEIGHT; // Ceiling height threshold with mic above 13' (ceiling-tile mics and CBC bars exempt — PIG-422 / CBC has its own 6'-12' range)
    // e1: Auto-lower ceiling mic height when it exceeds optimal height for ANY acoustics level.
    // PIG-295 AC2/AC3 (PIG-429): BMA360D is exempt from this generic path because
    // calculateDevicesMiddleware.maybeAutoLowerBma360d handles auto-lower for ceiling-tile mics
    // with a spec-precise target (the formula maxAllowableHeight). Leaving e1 enabled for BMA360D
    // would fire a second, slightly-off auto-lower and a duplicate toast.
    // CBC bars (isBarMic) are likewise exempt — see isBarMic comment above.
    const e1 = isCeilingMic && !isPendantMic && !isCeilingTileMic && !isBarMic && height > max && ceilingHeight > max;
    const e2 = isPendantMic && height > max;

    // NEW: Additional validation for ceiling microphone height constraints
    // Only apply these validations if we have valid height data and ceiling mic
    const hasValidHeightData = typeof height === 'number' && height > 0;
    const minimumMicHeight = toolConstants?.mounting?.minimumMicHeight || 1.8288; // 6 feet
    const maximumMountingDistance = toolConstants?.mounting?.maximumMountingDistance || 9.1; // 30 feet
    const heightTolerance = 0.001; // 1mm tolerance for floating point comparison

    // Only validate ceiling mic height if we have valid height data and it's a ceiling mic
    // (CBC bars excluded — their height range is enforced by heightRange in the setMicrophones
    // reducer, not by this TCM-X-style coverage formula)
    const e3 = hasValidHeightData && isCeilingMic && !isPendantMic && !isBarMic && height < minimumMicHeight; // Below 6 feet
    const e4 = hasValidHeightData && isCeilingMic && !isPendantMic && !isBarMic && height > (ceilingHeight + heightTolerance); // Above ceiling height (not allowed)
    const e5 = hasValidHeightData && isCeilingMic && !isPendantMic && !isBarMic && (ceilingHeight - height) > maximumMountingDistance; // Mounting distance too large
    const e6 = hasValidHeightData && isCeilingMic && !isPendantMic && !isBarMic && isFinite(max) && height > max; // Height exceeds optimal acoustic coverage

    // REMOVED: Auto-restore to ceiling logic
    // Users should have full control over height adjustments
    // System only auto-lowers when necessary (e1 for poor acoustics, e6 for exceeding optimal)
    const shouldRestoreToCeiling = false;

    const hasInfo = findKey(info, { 'code': 1100 });

    // Helper function to create message object
    const createMessage = (code, msg) => ({ code, msg });

    // Helper function to dispatch message based on modal state
    const dispatchMessage = (message) => {
      const action = isModalOpen ? 'setInfo' : 'setError';
      dispatch(ReducersIndex.uiReducers[action]({
        ...message,
        reset: !isModalOpen
      }));
    };

    // Helper function to dispatch the changes
    const dispatchFix = (payload) => {
      // Apply microphone changes
      const micUpdate = payload.update.value;

      // First clear the errors that trigger the model change
      dispatch(ReducersIndex.uiReducers.setError({ code: 1100 }));
      dispatch(ReducersIndex.uiReducers.setError({ code: 1110 }));

      // Then apply the microphone changes - always use direct setMicrophones for immediate UI update
      // Add bypass flags to prevent infinite validation loops (see MiddlewareActionMeta in types/middleware/actionMeta.ts)
      dispatch(ReducersIndex.container.setMicrophones({
        ...micUpdate,
        _fromMiddleware: true,
        _fromSyncMiddleware: true
      }));

      // Also dispatch setAreaToUpdate if not in modal for consistency with other validation
      if (!isModalOpen) {
        dispatch(ReducersIndex.container.setAreaToUpdate(payload));
      }

      // Apply a second time to ensure modelName doesn't get lost to resetArea
      setTimeout(() => {
        if (micUpdate.modelName) {
          dispatch(ReducersIndex.container.setMicrophones({
            modelName: micUpdate.modelName,
            _fromMiddleware: true,    // see MiddlewareActionMeta in types/middleware/actionMeta.ts
            _fromSyncMiddleware: true
          }));
        }
      }, 0);
    };
    // Handle ceiling microphone height constraints for user input
    // Only validate and show errors for ceiling microphones (not pendant, not CBC bars —
    // this block's formula is TCM-X/DCM-X-specific, see isBarMic comment above)
    if (isCeilingMic && !isPendantMic && !isBarMic && hasValidHeightData) {
      // Check if we have original input to validate (before clamping)
      const originalInput = area._originalHeightInput;
      const shouldCheckOriginal = originalInput !== undefined && originalInput !== null;

      // Skip validation if mic height equals ceiling (auto-adjusted by setCeilingHeight)
      const micHeightEqualsCeiling = Math.abs(height - ceilingHeight) < 0.0001;
      const wasAutoAdjusted = micHeightEqualsCeiling && !shouldCheckOriginal;

      // ONLY show validation toasts if we have original input (meaning user entered invalid value)
      // Don't show toasts for calculated heights, mic model changes, or auto-adjustments from ceiling changes
      if (!shouldCheckOriginal || wasAutoAdjusted) {
        // Skip the ceiling mic specific validation, but continue with other validations
      } else {
      
      // Use original input for validation if available, otherwise use current height
      const heightToValidate = shouldCheckOriginal ? originalInput : height;
      
      // Recalculate validation conditions with original input
      const e3_original = shouldCheckOriginal && heightToValidate < minimumMicHeight;
      const e4_original = shouldCheckOriginal && heightToValidate > (ceilingHeight + heightTolerance);
      const e5_original = shouldCheckOriginal && (ceilingHeight - heightToValidate) > maximumMountingDistance;
      const e6_original = shouldCheckOriginal && isFinite(max) && heightToValidate > max;

      // Clear the original input after validation (safe because we're only reading it once)
      if (shouldCheckOriginal) {
        // Use a timeout to clear it after validation to avoid state mutation issues
        setTimeout(() => {
          try {
            // This will be cleaned up on the next state update
            if (area._originalHeightInput !== undefined) {
              area._originalHeightInput = null;
            }
          } catch (e) {
            // Silently ignore if state is immutable
          }
        }, 0);
      }
      
      // Use the original input validation or fallback to regular validation
      const useE3 = shouldCheckOriginal ? e3_original : e3;
      const useE4 = shouldCheckOriginal ? e4_original : e4;
      const useE5 = shouldCheckOriginal ? e5_original : e5;
      const useE6 = shouldCheckOriginal ? e6_original : e6;

      // Check minimum height constraint (6 feet)
      // REMOVED: No longer showing error for minimum height - silently clamps to 6ft instead
      // if (useE3) {
      //   const message = `Microphone height cannot be lower than ${distanceToString(minimumMicHeight, isMeter)} (standing person clearance)`;
      //   console.log('🚨 SHOWING ERROR FOR e3:', message);
      //   showError(message);
      // }
      // Check maximum height constraint (cannot exceed ceiling)
      if (useE4) {
        const message = `Microphone height cannot exceed ${distanceToString(ceilingHeight, isMeter)} (ceiling height)`;
        showError(message);
      }
      // Check maximum mounting distance (30 feet)
      else if (useE5) {
        const message = `Mounting distance cannot exceed ${distanceToString(maximumMountingDistance, isMeter)}`;
        showError(message);
      }
      // Show warning for suboptimal coverage (non-blocking)
      else if (useE6 && isFinite(max)) {
        const message = `Current height may reduce coverage. Recommended maximum: ${distanceToString(max, isMeter)}`;
        showWarning(message);
      }
      } // End of the else block for original input validation
    }

    // DISABLED: Auto-restore to ceiling-mounted height
    // Removed to allow users full manual control over height adjustments
    // if (shouldRestoreToCeiling) {
    //   const adjustedHeight = ceilingMountedHeight;
    //   const payload = {
    //     area: state.container.activeTab,
    //     update: { parameter: 'microphones', value: { height: adjustedHeight, maxHeight: max } },
    //   };
    //   dispatchFix(payload);
    // }

    // Handle existing validation conditions
    if (e0 || e1 || e2) {
      
      if (e0) {
        // Physical ceiling limit exceeded (>= 14 feet): keep ceiling mic but lower to 13 feet max
        // The pipe mounting kit will be added automatically based on the mounting distance
        const adjustedHeight = MAX_CEILING_MIC_HEIGHT;
        const micUpdate = {
          height: adjustedHeight,
          maxHeight: max
        };
        const payload = {
          area: state.container.activeTab,
          update: { parameter: 'microphones', value: micUpdate },
        };
        dispatchFix(payload);
        if (isModalOpen) {
          dispatchMessage(createMessage(1100, `Ceiling height exceeds 14 feet. Microphone height is adjusting to ${distanceToString(adjustedHeight, isMeter)}.`));
        } else {
          showWarning(`Ceiling height exceeds 14 feet. Microphone height is adjusting to ${distanceToString(adjustedHeight, isMeter)}.`);
        }
      } else if (e1 && height > snappedMax) {
        // Acoustics limit: lower ceiling mic height to snappedMax instead of switching style
        // snappedMax matches the 0.5 ft pole increment the setMicrophones reducer applies,
        // so the warning message and displayed height always show the same value.
        const adjustedHeight = snappedMax;
        const payload = {
          area: state.container.activeTab,
          update: { parameter: 'microphones', value: { height: adjustedHeight, maxHeight: max } },
        };
        dispatchFix(payload);
        const micTypeName = 'Ceiling microphone';
        if (isModalOpen) {
          dispatchMessage(createMessage(1110, `${micTypeName} is too high to pick up any talkers at given room acoustics.\nAdjusted microphone height to ${distanceToString(adjustedHeight, isMeter)}.`));
        } else {
          showWarning(`${micTypeName} is too high to pick up any talkers at given room acoustics. Adjusting microphone height to ${distanceToString(adjustedHeight, isMeter)}.`);
        }
      };

      if (e2) {
        // For pendant mics, ensure height doesn't exceed the max for their coverage
        const adjustedHeight = Math.min(max, 2.4384); // Pendant mics max at 8 feet
        const payload = {
          area: state.container.activeTab,
          update: {
            parameter: 'microphones',
            value: {height: adjustedHeight}
          },
        };
        dispatchFix(payload);
        if (!hasInfo) {
          // Show both modal message and toast based on modal state
          const micTypeName = 'Pendant microphone';
          if (isModalOpen) {
            const message = createMessage(1110, `${micTypeName} is too high to pick up any talkers at given room acoustics.\nAdjusted microphone height to ${distanceToString(adjustedHeight, isMeter)}.`);
            dispatchMessage(message);
          } else {
            showWarning(`${micTypeName} is too high to pick up any talkers at given room acoustics. Adjusting microphone height to ${distanceToString(adjustedHeight, isMeter)}.`);
          }
        }
      };
    }

    // Validate bar microphone heights (CBC 2500 models)
    const modelName = area.microphones?.modelName || '';
    const isCBCBar = micCategories.CBC.has(model);

    if (isCBCBar && hasValidHeightData) {
      const minimumBuffer = 0.1524; // 0.5 feet minimum buffer from ceiling
      const maxBarHeight = ceilingHeight - minimumBuffer;
      const originalInput = area._originalHeightInput;
      const shouldCheckOriginal = originalInput !== undefined && originalInput !== null;
      const heightToValidate = shouldCheckOriginal ? originalInput : height;

      // Check if bar height exceeds ceiling - 0.5'
      if (heightToValidate > maxBarHeight) {
        const adjustedHeight = maxBarHeight;
        const payload = {
          area: state.container.activeTab,
          update: {
            parameter: 'microphones',
            value: { height: adjustedHeight }
          },
        };
        dispatchFix(payload);

        if (shouldCheckOriginal) {
          if (isModalOpen) {
            const message = createMessage(1110, `Mounting height can't be higher than ceiling height minus a minimum mounting buffer (0.5').\nAdjusted ${modelName} height to ${distanceToString(adjustedHeight, isMeter)}.`);
            dispatchMessage(message);
          } else {
            showWarning(`Mounting height can't be higher than ceiling height minus a minimum mounting buffer (0.5'). Adjusting ${modelName} height to ${distanceToString(adjustedHeight, isMeter)}.`);
          }
        }
      } else {
        // Clear error when height is valid
        dispatch(ReducersIndex.uiReducers.setError({ code: 1110 }));
      }
    } else if (!isCeilingMic && !micCategories.BAR.has(model)) {
      // Clear error states when no issues detected for other mic types
      dispatch(ReducersIndex.uiReducers.setError({ code: 1100 }));
      dispatch(ReducersIndex.uiReducers.setError({ code: 1110 }));
      // The ceiling mic height error clearing is now handled in the validation block above
    };

    // NOTE: Auto-recalculation of CBC bar height is handled in ceilingHeight validation
    // Do NOT auto-recalculate here, as it would override manual height adjustments
    } finally {
      // Always clear the flag, even if an error occurred
      isValidatingMicrophones = false;
    }
  },

  /**
   * Validates the CEILING expansion microphone (dcm_x) height against room acoustics.
   * @function
   * @param {function} dispatch - The Redux dispatch function.
   * @param {Object} state - The current Redux state.
   *
   * @description
   * Mirrors `validate.microphones`'s e1 auto-lower behavior for the primary ceiling mic
   * (tcm_x): dcm_x shares tcm_x's exact micData array, so the same
   * side(micDataValue, 1) + talkerHeight coverage formula, snapped to the same 0.5' pole
   * increment, gives the ceiling expansion mic the identical "optimal" height the primary
   * would get for the same room/acoustics combination — instead of staying flush-mounted
   * at the raw ceiling height regardless of acoustics.
   * Table expansion (ttm_x) isn't ceiling-mounted, so this is a no-op unless
   * `expansionMicType === 'ceiling'`.
   */
  expansionMicrophones: (dispatch, state) => {
    // Prevent infinite validation loop - skip if we're already validating
    if (isValidatingExpansionMicrophones) {
      return;
    }

    isValidatingExpansionMicrophones = true;

    try {
      const { units, isModalOpen } = state.ui;
      const isMeter = units === 'meters';
      const area = state.container.areas[state.container.activeTab];

      if (!area || area.expansionMicType !== 'ceiling' || !area.expansionMicrophones || !area.ceilingHeight) {
        return;
      }

      const { expansionMicrophones: { model, height }, acoustics: currentAcoustics, ceilingHeight: { min: ceilingHeight } } = area;
      const { equipment: { microphones }, acoustics, area: { talkerHeight } } = toolIndex[state.container.currentTool].constants;
      const micEquipment = microphones[model];

      if (!micEquipment || !micEquipment.micData || typeof height !== 'number') {
        return;
      }

      const acousticsIndex = indexOf(acoustics, currentAcoustics);
      const micDataValue = micEquipment.micData[acousticsIndex];
      if (acousticsIndex === -1 || !micDataValue) {
        return;
      }

      const max = round(side(micDataValue, 1) + talkerHeight, 4);
      // Snap DOWN to the same 0.5 ft pole increment setExpansionMicrophones/setMicrophones use
      // (a max constraint is never rounded up — see e1 above).
      const poleIncrement = 0.1524;
      const snappedMax = snapDownToIncrement(max, poleIncrement);

      // Acoustics limit: lower the expansion ceiling mic height to snappedMax, same as e1
      // does for the primary ceiling mic in validate.microphones above.
      if (isFinite(max) && height > max && ceilingHeight > max && height > snappedMax) {
        const adjustedHeight = snappedMax;
        dispatch(ReducersIndex.container.setExpansionMicrophones({
          height: adjustedHeight,
          _fromMiddleware: true,
          _fromSyncMiddleware: true,
        }));
        const message = `Ceiling expansion microphone is too high to pick up any talkers at given room acoustics. Adjusting microphone height to ${distanceToString(adjustedHeight, isMeter)}.`;
        if (!isModalOpen) {
          showWarning(message);
        }
      }
    } finally {
      isValidatingExpansionMicrophones = false;
    }
  },

  /**
   * Alias so switching TO ceiling expansion (`setExpansionMicType`) immediately validates
   * the freshly-snapshotted height against acoustics, exactly like editing
   * `setExpansionMicrophones` directly does.
   * @function
   * @param {function} dispatch - The Redux dispatch function.
   * @param {Object} state - The current Redux state.
   */
  expansionMicType: (dispatch, state) => {
    validate.expansionMicrophones(dispatch, state);
  },

  /** Validate microiphones when area is updated
   * Forces re-validation of dimensions and ceiling height
   */
  updateArea: (dispatch, state) => {
    validate.dimensions(dispatch, state);
    validate.ceilingHeight(dispatch, state);
    validate.microphones(dispatch, state);
    validate.expansionMicrophones(dispatch, state);
  },

  /**
   * Validates the room acoustics.
   * @function
   * @param {function} dispatch - The Redux dispatch function.
   * @param {Object} state - The current Redux state.
   *
   * @description
   * Clears any existing info messages and re-validates the microphone setup as acoustics affect microphone performance.
   */
  acoustics: (dispatch, state) => {
    // Clear info messages first
    dispatch(ReducersIndex.uiReducers.setInfo());

    // Clear mic-related errors before revalidation to ensure clean state transitions
    dispatch(ReducersIndex.uiReducers.setError({ code: 1100 }));
    dispatch(ReducersIndex.uiReducers.setError({ code: 1110 }));

    // Revalidate microphones with new acoustics settings
    validate.microphones(dispatch, state);
    validate.expansionMicrophones(dispatch, state);
  },

  /**
   * Validates the room acoustics.
   * @function
   * @param {function} dispatch - The Redux dispatch function.
   * @param {Object} state - The current Redux state.
   * 
   * @description
   * Clears any existing info messages and re-validates the microphone setup as acoustics affect microphone performance.
   */
  speakers: (dispatch, state) => {
    const { speakers: { model, height }, ceilingHeight: { min: ceilingHeight } } = state.container.areas[state.container.activeTab];
    const { equipment: { speakers: { [model]: { isCeilingSpkr = false, isBarSpkr = false } } } } = toolIndex[state.container.currentTool].constants;
    if (isCeilingSpkr && height !== ceilingHeight) {
      dispatch(ReducersIndex.container.setSpeakers({ height: ceilingHeight }))
    }
  },

  /**
  * @function productFamily validation
  * @description Validates product family selection against device quantities and enforces system limits.
  * 
  * @param {Function} dispatch - Redux dispatch function
  * @param {Object} state - Current Redux state
  * @param {Object} state.container.areas - Areas containing device configurations
  * @param {Object} state.container.activeTab - Currently active area ID
  * 
  * @description
  * Performs validation by:
  * 1. Checking device quantities:
  *    - Takes maximum of either qty field or actual layout length
  *    - Validates against product family limits
  * 2. For Devio systems specifically:
  *    - Enforces maximum of 8 microphones
  *    - Enforces maximum of 16 speakers
  * 3. Handles limit violations by:
  *    - Displaying error message
  *    - Automatically switching to Tesira family
  * 
  * @example
  * // When Devio system exceeds limits
  * // Shows error: "Devio does not support more than 8 microphones."
  * // Automatically switches to Tesira family
  * 
  * @note
  * - Uses max of qty field and layout length for accurate counts
  * - Provides automatic fallback to Tesira for exceeded limits
  * - Error code 2000 indicates product family limit violation
  */
  productFamily: (dispatch, state) => {
    const area = state.container.areas[state.container.activeTab];
    if (!area?.microphones || !area?.speakers) return;
    const { microphones: { qty: micQty, layout: micLayout = [] } = { qty: 0, layout: [] }, speakers: { qty: spkrQty, layout: spkrLayout = [] } = { qty: 0, layout: [] }, productFamily } = area;
    const isDevio = productFamily === 'Devio';
    const totalMics = micLayout.length;
    const totalSpkrs = spkrLayout.length;
    const e1 = isDevio && (totalMics > 8 || totalSpkrs > 16)
    if (e1) {
      // Show as toast message and clear error
      showWarning(`Devio does not support more than ${totalMics > 8 ? '8 microphones' : '16 speakers'}. Switching product family to Tesira.`);
      dispatch(ReducersIndex.uiReducers.setError({ code: 2000 }));
      dispatch(ReducersIndex.container.setProductFamily('Tesira'));
    }
  },

  /**
   * Validates ceiling tile quantities.
   * @function
   * @param {function} dispatch - The Redux dispatch function.
   * @param {Object} state - The current Redux state.
   * 
   * @description
   * Checks if the ceiling tile quantities exceed what can physically fit in the room.
   * If the quantities are too large, automatically adjusts them to the maximum that fits
   * and updates both the state and UI inputs.
   */
  ceilingTiles: (dispatch, state) => {
    try {
      // Enhanced safety checks for state structure
      if (!state?.container?.areas || !state?.container?.activeTab) {
        console.warn('ceilingTiles validation: Missing container state');
        return;
      }

      const area = state.container.areas[state.container.activeTab];
      if (!area || !area.ceilingTiles || !area.dimensions) {
        return;
      }
      
      const { dimensions, ceilingTiles } = area;
      const { length, width } = dimensions;
      const { tile, qty } = ceilingTiles;
      
      // Enhanced validation for dimensions
      if (typeof length !== 'number' || typeof width !== 'number' || length <= 0 || width <= 0) {
        console.warn('ceilingTiles validation: Invalid room dimensions');
        return;
      }
      
      // Skip validation if no ceiling tiles are selected or no quantities set
      if (!tile || tile === 'no_ceiling_tiles' || !qty || typeof qty !== 'object') {
        // Clear any existing ceiling tile errors for valid no-tile state
        dispatch(ReducersIndex.uiReducers.setError({
          code: 1500,
        }));
        return;
      }
      
      // Enhanced validation for quantity object
      if (typeof qty.x !== 'number' || typeof qty.y !== 'number' || qty.x < 0 || qty.y < 0) {
        console.warn('ceilingTiles validation: Invalid quantity values', qty);
        return;
      }
      
      // Get tile dimensions from constants - enhanced safety checks
      const toolConstants = toolIndex[state.container.currentTool];
      if (!toolConstants?.constants?.ceilingTiles) {
        console.warn('ceilingTiles validation: Missing tool constants');
        return;
      }
      
      const tileDimensions = toolConstants.constants.ceilingTiles[tile];
      if (!tileDimensions || 
          typeof tileDimensions.x !== 'number' || 
          typeof tileDimensions.y !== 'number' || 
          tileDimensions.x <= 0 || 
          tileDimensions.y <= 0) {
        console.warn('ceilingTiles validation: Invalid tile dimensions for tile type:', tile, tileDimensions);
        return;
      }
      
      const maxX = calcMaxTilesFit(length, tileDimensions.x);
      const maxY = calcMaxTilesFit(width,  tileDimensions.y);
      
      // Check if current quantities exceed the maximum
      const exceededX = qty.x > maxX;
      const exceededY = qty.y > maxY;
      
      if (exceededX || exceededY) {
        // Create corrected quantities with enhanced bounds checking
        const correctedQty = {
          x: Math.max(1, Math.min(Math.floor(qty.x), maxX)),
          y: Math.max(1, Math.min(Math.floor(qty.y), maxY))
        };
        
        // Only update if the corrected values are actually different
        if (correctedQty.x !== qty.x || correctedQty.y !== qty.y) {
          // Use setTimeout to prevent infinite loop by delaying the dispatch
          setTimeout(() => {
            try {
              dispatch(ReducersIndex.container.setCeilingTiles({ 
                qty: correctedQty,
                checked: [] // Reset checked tiles when quantities change
              }));
              
              // Optionally log the correction for debugging
              console.log(`Ceiling tile quantities corrected from (${qty.x}, ${qty.y}) to (${correctedQty.x}, ${correctedQty.y})`);
            } catch (dispatchError) {
              console.error('Error dispatching ceiling tile correction:', dispatchError);
            }
          }, 0);
        }
      } else {
        // Clear any existing ceiling tile errors for valid state
        // Only clear if error exists
        if (has(state.ui.error, '1500')) {
          dispatch(ReducersIndex.uiReducers.setError({
            code: 1500,
          }));
        }
      }
    } catch (error) {
      console.error('Critical error in ceiling tile validation:', error);
      // Try to clear any error state to prevent UI issues
      try {
        if (has(state.ui.error, '1500')) {
          dispatch(ReducersIndex.uiReducers.setError({
            code: 1500,
          }));
        }
      } catch (clearError) {
        console.error('Failed to clear ceiling tile error state:', clearError);
      }
    }
  },

}




export default validate;
